import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import cookie from 'js-cookie'

import s from './HomePage.module.css'
import { EnterIcon, LocationIcon, Logo, AppStore } from 'components/common'
import Select, { ControlProps, components } from 'react-select'
import { Region } from 'components/common/types'

type RegionOption = {
  slug: Region['name']
  value: Region['slug']
}

type ControlCustomProps = {
  children: React.ReactNode
  selectProps: {
    selectedRegion: RegionOption
    enterClickHandler: () => void
  }
} & ControlProps

const Control = ({ children, selectProps, ...rest }: ControlCustomProps) => (
  <>
    <components.Control
      {...rest}
      selectProps={selectProps}
      className={s.control}
    >
      <span>
        <LocationIcon className={s.locationIcon} />
      </span>
      {children}
    </components.Control>
    <div
      className={s.enterIconWrapper}
      style={{
        pointerEvents: !!selectProps.selectedRegion ? 'unset' : 'none'
      }}
      onClick={selectProps.enterClickHandler}
    >
      <EnterIcon className={s.enterIcon} />
    </div>
  </>
)

const customStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: '30px',
    marginTop: '20px',
    width: '100%',
    height: '50px',
    fontWeight: 700,
    fontSize: '2rem',
    padding: '0 5px'
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '2rem',
    fontWeight: 500
  })
}

type Props = {
  regions: Region[]
  serviceType:
    | 'rides'
    | 'groceries'
    | 'package-delivery'
    | 'mobile-phone-topups'
    | 'bill-payment'
    | ''
  title: string
  mainImageUrl: string
}

const Homepage = ({ regions, serviceType, title, mainImageUrl }: Props) => {
  const [isIOS, setIsIOS] = useState(false)
  const router = useRouter()
  const [selectedRegion, setSelectedRegion] = useState<RegionOption | null>(
    null
  )

  const sortedRegions = [...regions].sort(function (a, b) {
    if (a.name < b.name) {
      return -1
    }
    if (a.name > b.name) {
      return 1
    }
    return 0
  })

  const allRegions = sortedRegions.map((item) => ({
    value: item.slug,
    label: item.name
  }))

  useEffect(() => {
    const cookieSelectedRegion = cookie.get('selectedRegion')

    if (cookieSelectedRegion) {
      setSelectedRegion(JSON.parse(cookieSelectedRegion))
    }
  }, [])

  useEffect(() => {
    const isTouchDevice =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0

    setIsIOS(isTouchDevice)
  }, [])

  const selectChangeHandler = (region) => setSelectedRegion(region)

  const enterClickHandler = () => {
    cookie.set('selectedRegion', `${JSON.stringify(selectedRegion)}`)
    router.push(`services/${selectedRegion!.value}/${serviceType}`)
  }

  return (
    <div>
      <section>
        <div className={s.regionWrapper}>
          <div className={s.firstImage}>
            <img src={mainImageUrl} />
          </div>
          <div className={s.searchRestaurantWrapper}>
            <div className={s.inputWrapper}>
              <h2>{title}</h2>
              <div className={s.selectWrapper}>
                <Select
                  styles={customStyles}
                  options={allRegions}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  value={selectedRegion}
                  onChange={selectChangeHandler}
                  enterClickHandler={enterClickHandler}
                  selectedRegion={selectedRegion}
                  placeholder="Select order location"
                  components={{
                    DropdownIndicator: null,
                    Control,
                    IndicatorSeparator: null
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={s.appSectionWrapper}>
        <div className={s.appSection}>
          <div className={s.appSectionTitle}>
            <h2>Anything and Everything, Delivered FAST</h2>

            <div>Check out what you can do with this app!</div>
          </div>

          <div className={s.appContent}>
            <div className={s.appCellWrapper}>
              <div className={s.appCell}>
                <img src={'eats.png'} />

                <h4>The Best Local Restaurants</h4>
                <p>All the best local restaurants, all in one app!</p>
              </div>

              <div className={s.appCell}>
                <img src={'ride.png'} />

                <h4>RideShare</h4>
                <p>Zip around the island in a safe and private car!</p>
              </div>
            </div>

            <div className={s.appPictureWrapper}>
              <picture>
                <source media="(max-width: 1349px)" srcSet="iphone_sm.png" />
                <source media="(min-width: 1350px)" srcSet="iphone_md.png" />
                <img src="iphone_md.png" />
              </picture>
            </div>

            <div className={s.appCellWrapper}>
              <div className={s.appCell}>
                <img src={'shop.png'} />

                <h4>Unlimited Groceries</h4>
                <p>Order all you need, we'll do the shopping for you!</p>
              </div>

              <div className={s.appCell}>
                <img src={'pack.png'} />

                <h4>Package Delivery</h4>
                <p>
                  Yup, simply make a booking in the app and we'll deliver your
                  packages when you're ready for them!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={s.aboutSectionWrapper}>
        <h2>Join CaribeEats</h2>

        <div className={s.aboutSection}>
          <div className={s.aboutCell}>
            <div className={s.aboutIconWrapper}>
              <img src="/courier.jpg" alt="Become a Driver" width="100%" />
            </div>
            <div className={s.aboutInfoCell}>
              <h3>Become a Driver</h3>

              <h4>
                As a delivery driver, you'll make reliable money—working
                anytime, anywhere.
              </h4>

              <a
                href="https://driver.caribeeats.com/"
                target="_blank"
                className={s.aboutNavigator}
              >
                Start earning
              </a>
            </div>
          </div>

          <div className={s.aboutCell}>
            <div className={s.logoIconWrapper}>
              <Logo width="166px" height="61px" className={s.aboutIcon} />
            </div>
            <div className={s.aboutInfoCell}>
              <h3>Try the App</h3>

              <h4>
                Experience the best your neighborhood has to offer, all in one
                app.
              </h4>

              <div className={s.appLinkWrapper}>
                <a
                  href={'https://apps.apple.com/us/app/caribeeats/id1462589124'}
                  target="_blank"
                  className={s.appStore}
                >
                  <AppStore width={136} height={41} />
                </a>

                {!isIOS && (
                  <a
                    target="_blank"
                    href={
                      'https://play.google.com/store/apps/details?id=com.caribeeats.consumer'
                    }
                  >
                    <img
                      className={s.googlePlay}
                      src="/GooglePlay.png"
                      alt="google-play"
                      width={136}
                      height={41}
                    />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className={s.aboutCell}>
            <div className={s.aboutIconWrapper}>
              <img src="/business.jpg" alt="Become A Partner" width="100%" />
            </div>
            <div className={s.aboutInfoCell}>
              <h3>Become a Partner</h3>

              <h4>
                Grow your business and reach new customers by partnering with
                us.
              </h4>

              <a
                href="https://business.caribeeats.com/"
                target="_blank"
                className={s.aboutNavigator}
              >
                Sign up your store
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Homepage
